import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Loader2, Send, X, ArrowLeft } from "lucide-react";
import { submitLead } from "@/lib/api";

const QRS = [
  ["PayPal", "Terrell Turner Ministries", "/assets/qr-3.webp"],
  ["Venmo", "@Prophettt", "/assets/qr-2.webp"],
  ["Cash App", "Terrell Turner Ministries", "/assets/qr-1.jpeg"],
];

const EMPTY = {
  name: "", email: "", phone: "", address: "", city: "", state: "", zip: "",
  organization: "", referral: "", commitment: false,
};

export default function WarfareRegistration({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) =>
    setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  useEffect(() => {
    if (open) document.documentElement.style.overflow = "hidden";
    else document.documentElement.style.overflow = "";
    return () => { document.documentElement.style.overflow = ""; };
  }, [open]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitLead({ type: "warfare_registration", ...form });
      toast.success("Registration received! Watch your email — your Zoom link is on the way after review.");
      setForm(EMPTY);
      onClose();
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div data-testid="warfare-registration-modal" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }} onClick={onClose}
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/85 backdrop-blur-md px-4 py-8">
          <motion.div initial={{ scale: 0.92, y: 40 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 20, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} onClick={(e) => e.stopPropagation()}
            data-lenis-prevent
            className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto overscroll-contain rounded-3xl bg-[#0B0E16] border border-red-500/25 shadow-[0_30px_100px_-20px_rgba(239,68,68,0.35)] p-6 sm:p-9">
            <button data-testid="warfare-registration-back"
              onClick={() => {
                onClose();
                if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.2 });
                else window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3.5 py-2 text-xs font-semibold text-slate-300 hover:text-white hover:bg-white/10 transition-colors">
              <ArrowLeft size={15} /> Back to Home
            </button>
            <button data-testid="warfare-registration-close" onClick={onClose} aria-label="Close"
              className="absolute top-4 right-4 flex h-9 w-9 items-center justify-center rounded-full bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-colors">
              <X size={17} />
            </button>

            <p className="mt-8 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-red-400">Spiritual Warfare Classes</p>
            <h3 className="mt-2 font-display text-2xl sm:text-3xl font-extrabold text-white">
              Six-Week Intensive — <span className="text-crimson-gold">Registration</span>
            </h3>
            <p className="mt-3 text-sm sm:text-base text-slate-300 leading-relaxed">
              Tuesdays, November 3 – December 8, 2026 · 7:00 PM Eastern · Live on Zoom.
              Your Zoom link will be sent after your registration is received.
            </p>

            <form data-testid="warfare-registration-form" onSubmit={onSubmit} className="mt-8 grid gap-4">
              <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">Your Information</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="warfare-reg-name-input" required value={form.name} onChange={set("name")} placeholder="Full name *" className="field-input" />
                <input data-testid="warfare-reg-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="Email address *" className="field-input" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="warfare-reg-phone-input" value={form.phone} onChange={set("phone")} placeholder="Phone" className="field-input" />
                <input data-testid="warfare-reg-affiliation-input" value={form.organization} onChange={set("organization")} placeholder="Church / Ministry affiliation (optional)" className="field-input" />
              </div>
              <input data-testid="warfare-reg-address-input" value={form.address} onChange={set("address")} placeholder="Mailing address" className="field-input" />
              <div className="grid grid-cols-3 gap-4">
                <input data-testid="warfare-reg-city-input" value={form.city} onChange={set("city")} placeholder="City" className="field-input" />
                <input data-testid="warfare-reg-state-input" value={form.state} onChange={set("state")} placeholder="State" className="field-input" />
                <input data-testid="warfare-reg-zip-input" value={form.zip} onChange={set("zip")} placeholder="Zip" className="field-input" />
              </div>
              <input data-testid="warfare-reg-referral-input" value={form.referral} onChange={set("referral")} placeholder="How did you hear about this class?" className="field-input" />

              <div className="mt-2 rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5 sm:p-6">
                <p className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-300">Class Payment — $55.00</p>
                <p className="mt-2 text-sm sm:text-base text-slate-200 leading-relaxed">
                  The investment for the six-week intensive is <strong className="text-amber-300">$55.00</strong>.
                  Please submit your payment using any of the methods below, and kindly include your full
                  name in the payment note so your registration can be matched.
                </p>
                <div className="mt-5 grid grid-cols-3 gap-3 sm:gap-4">
                  {QRS.map(([name, handle, src]) => (
                    <div key={name} data-testid={`warfare-reg-pay-${name.toLowerCase().replace(" ", "-")}`} className="text-center">
                      <div className="rounded-xl bg-white p-2 shadow-[0_8px_25px_-10px_rgba(0,0,0,0.6)]">
                        <img src={src} alt={`${name} QR code — ${handle}`} className="w-full aspect-square object-contain" />
                      </div>
                      <p className="mt-2 text-xs sm:text-sm font-bold text-white">{name}</p>
                      <p className="text-[10px] sm:text-xs text-slate-400 break-all">{handle}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-xs sm:text-sm text-slate-400 leading-relaxed">
                  Your seat is confirmed once payment is received. Your Zoom link will follow by email.
                </p>
              </div>

              <label data-testid="warfare-reg-commitment-label" className="mt-2 flex items-start gap-3 rounded-xl border border-red-500/25 bg-red-500/5 p-4 cursor-pointer">
                <input data-testid="warfare-reg-commitment-checkbox" type="checkbox" required checked={form.commitment} onChange={set("commitment")}
                  className="mt-1 h-4 w-4 shrink-0 accent-red-500" />
                <span className="text-sm text-slate-200 leading-relaxed">
                  I understand this is a six-week intensive course held on Zoom, and I commit to attending each Tuesday
                  session from November 3 through December 8, 2026, at 7:00 PM Eastern.
                </span>
              </label>

              <button data-testid="warfare-reg-submit-button" type="submit" disabled={loading}
                className="group mt-2 inline-flex w-fit items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-amber-400 to-red-500 text-white text-base font-bold uppercase tracking-[0.12em] shadow-[0_10px_35px_-10px_rgba(239,68,68,0.6)] hover:brightness-110 hover:scale-[1.03] active:scale-95 disabled:opacity-60 transition-all duration-300">
                {loading ? <Loader2 size={17} className="animate-spin" /> : <Send size={17} className="transition-transform duration-300 group-hover:translate-x-1" />}
                {loading ? "Submitting..." : "Submit Registration"}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}
